'use client';

import { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import { useImagePicker } from '../../helpers/imagePicker';
import { useRest, useTranslation } from '../../services';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

type UseUploadImageOptions = {
  allowGif?: boolean;
};

const stripDataUrlPrefix = (value: string): string =>
  value.includes('base64,') ? value.split('base64,')[1] : value;

const isGifBase64 = (base64String: string): boolean => {
  try {
    const signature = atob(base64String).slice(0, 6);
    return signature === 'GIF87a' || signature === 'GIF89a';
  } catch {
    return false;
  }
};

const compressBase64Image = async (base64String: string): Promise<string> => {
  // Convertir base64 en Blob
  const fetchResponse = await fetch(`data:image/jpeg;base64,${base64String}`);
  const blob = await fetchResponse.blob();

  // Si l'image est déjà assez petite, on la retourne telle quelle
  if (blob.size <= MAX_FILE_SIZE) {
    return base64String;
  }

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = `data:image/jpeg;base64,${base64String}`;

    image.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = image;

      // Réduire les dimensions si nécessaire
      const maxDimension = 1200;
      if (width > maxDimension || height > maxDimension) {
        const ratio = maxDimension / Math.max(width, height);
        width = Math.floor(width * ratio);
        height = Math.floor(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Cannot get canvas context'));
        return;
      }

      ctx.drawImage(image, 0, 0, width, height);

      // Compression avec une qualité fixe de 0.7
      const compressed = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
      resolve(compressed);
    };

    image.onerror = () => reject(new Error('Failed to load image'));
  });
};

const prepareImageForUpload = async (base64String: string): Promise<string> => {
  // Canvas compression would flatten animated GIFs to a single JPEG frame.
  if (isGifBase64(base64String)) {
    return base64String;
  }
  return compressBase64Image(base64String);
};

const isUserCancellation = (error: unknown): boolean => {
  const e = error as { message?: string };
  return (
    e?.message === 'User cancelled photos app' ||
    e?.message === 'User rejected permissions'
  );
};

export const useUploadImage = (
  imageUsage: number,
  target_id?: number,
  options?: UseUploadImageOptions
) => {
  const { post } = useRest();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const pickImage = useImagePicker();
  const allowGif = options?.allowGif ?? false;

  const uploadImage = useCallback(async () => {
    setLoading(true);
    try {
      const base64 = await pickImage({ source: 'photos' });
      if (!base64) {
        setLoading(false);
        return;
      }

      if (!allowGif && isGifBase64(base64)) {
        setLoading(false);
        enqueueSnackbar(
          t(
            "Les GIF ne sont pas supportés. Merci de télécharger d'autres formats d'images."
          ),
          {
            variant: 'error',
          }
        );
        return;
      }

      const compressedImage = await prepareImageForUpload(base64);

      const response = await post('/uploads/images/', {
        image: compressedImage,
        usage: imageUsage,
        target_id,
      });

      setLoading(false);
      return response.image;
    } catch (error) {
      setLoading(false);
      if (isUserCancellation(error)) {
        return;
      }
      console.error('Error while uploading image:', error);
      throw error;
    }
  }, [pickImage, post, imageUsage, target_id, allowGif, enqueueSnackbar, t]);

  const captureImage = useCallback(async () => {
    try {
      const base64 = await pickImage({ source: 'prompt' });
      if (!base64) {
        return null;
      }

      if (isGifBase64(base64)) {
        if (!allowGif) {
          enqueueSnackbar(
            t(
              "Les GIF ne sont pas supportés. Merci de télécharger d'autres formats d'images."
            ),
            {
              variant: 'error',
            }
          );
          return null;
        }
        return `data:image/gif;base64,${base64}`;
      }

      return `data:image/png;base64,${base64}`;
    } catch (error) {
      if (isUserCancellation(error)) {
        return null;
      }
      console.error('Error while capturing image:', error);
      throw error;
    }
  }, [pickImage, t, enqueueSnackbar, allowGif]);

  const uploadCapturedImage = useCallback(
    async (base64Image: string) => {
      setLoading(true);
      try {
        const rawBase64 = stripDataUrlPrefix(base64Image);

        if (!allowGif && isGifBase64(rawBase64)) {
          setLoading(false);
          enqueueSnackbar(
            t(
              "Les GIF ne sont pas supportés. Merci de télécharger d'autres formats d'images."
            ),
            {
              variant: 'error',
            }
          );
          return null;
        }

        const compressedImage = await prepareImageForUpload(rawBase64);

        const response = await post('/uploads/images/', {
          image: compressedImage,
          usage: imageUsage,
          target_id,
        });

        setLoading(false);
        return response.image;
      } catch (error) {
        setLoading(false);
        console.error('Error while uploading image:', error);
        throw error;
      }
    },
    [post, imageUsage, target_id, allowGif, enqueueSnackbar, t]
  );

  return {
    uploadImage,
    captureImage,
    uploadCapturedImage,
    uploadLoading: loading,
  };
};
